// src/encounter/lib/to-wrap-up-request.ts
// Build the CreateWrapUp / UpdateWrapUp body for a follow-up-scheduler change. Preserves the other
// Wrap-Up fields the record already carries (instructions / notePlainText) so a follow-up-only save
// never wipes patient instructions. Carries the existing `id` when present → the hook PUT-updates
// instead of creating a duplicate.
import type { WrapUpModel, FollowUpUnit } from '@/types/wrap-up-api';

export interface FollowUpInput {
  /** 0 = no follow-up (clears it); the backend only schedules when count is non-zero. */
  count: number;
  unit: FollowUpUnit;
}

/** Derive the follow-up form value from a loaded wrap-up record — falling back to the default
 *  (0 / Weeks) when the record is null or carries no follow-up. Used to reset the form when the
 *  encounter changes, so a prior encounter's value can't linger (Codex P2). */
export function followUpFromWrapUp(record: WrapUpModel | null | undefined): FollowUpInput {
  return {
    count: record?.followupCount ?? 0,
    unit: (record?.followupCountUnit as FollowUpUnit) || 'Weeks',
  };
}

export function buildWrapUpFollowUpRequest(
  input: FollowUpInput,
  ctx: { patientId: string; encounterId: string; existing?: WrapUpModel | null },
): WrapUpModel {
  const existing = ctx.existing ?? null;
  return {
    ...(existing?.id ? { id: existing.id } : {}),
    subject: ctx.patientId,
    encounter: ctx.encounterId,
    // Preserve the non-follow-up Wrap-Up fields (other slices own them) — a follow-up save must
    // not blank them out on the round-trip.
    instructions: existing?.instructions ?? null,
    notePlainText: existing?.notePlainText ?? null,
    followupCount: input.count,
    followupCountUnit: input.unit,
    isDeleted: false,
  };
}

/** Instructions-only save — preserves the follow-up fields the scheduler owns (mirror of
 *  buildWrapUpFollowUpRequest's preserve-others rule, PROD-1588 S7 wiring). */
export function buildWrapUpInstructionsRequest(
  instructions: string,
  ctx: { patientId: string; encounterId: string; existing?: WrapUpModel | null },
): WrapUpModel {
  const existing = ctx.existing ?? null;
  return {
    ...(existing?.id ? { id: existing.id } : {}),
    subject: ctx.patientId,
    encounter: ctx.encounterId,
    instructions: instructions === '' ? null : instructions,
    notePlainText: existing?.notePlainText ?? null,
    followupCount: existing?.followupCount ?? 0,
    followupCountUnit: (existing?.followupCountUnit as FollowUpUnit) || 'Weeks',
    isDeleted: false,
  };
}
